export interface DocumentRequirement {
  key: string;
  label: string;
  required: boolean;
  public: boolean;
  description?: string;
}

export interface TradeRequirements {
  required_docs: string[];
  optional_docs: string[];
}

export const COMMON_REQUIRED_DOCS: DocumentRequirement[] = [
  {
    key: 'public_liability_insurance',
    label: 'Public Liability Insurance',
    required: true,
    public: false,
    description: 'Valid public liability insurance certificate'
  },
  {
    key: 'profile_photo',
    label: 'Profile Photo',
    required: true,
    public: true,
    description: 'This will be shown to customers so they know who\'s arriving'
  }
];

export const DOCUMENT_DEFINITIONS: Record<string, DocumentRequirement> = {
  public_liability_insurance: {
    key: 'public_liability_insurance',
    label: 'Public Liability Insurance',
    required: true,
    public: false,
    description: 'Valid public liability insurance certificate'
  },
  profile_photo: {
    key: 'profile_photo',
    label: 'Profile Photo',
    required: true,
    public: true,
    description: 'Professional photo shown to customers'
  },
  portfolio_photos: {
    key: 'portfolio_photos',
    label: 'Portfolio Photos',
    required: false,
    public: false,
    description: 'Examples of your previous work (optional)'
  },
  qualification_or_experience_proof: {
    key: 'qualification_or_experience_proof',
    label: 'Qualification or Experience Proof',
    required: true,
    public: false,
    description: 'Certificates, membership, or evidence of experience'
  },
  gas_safe_id: {
    key: 'gas_safe_id',
    label: 'Gas Safe ID',
    required: false,
    public: false,
    description: 'Only required if offering gas-related services'
  },
  electrical_qualification: {
    key: 'electrical_qualification',
    label: 'Electrical Qualification',
    required: true,
    public: false,
    description: 'Valid electrical qualification certificate'
  },
  niceic_napit_membership: {
    key: 'niceic_napit_membership',
    label: 'NICEIC/NAPIT Membership',
    required: false,
    public: false,
    description: 'Membership certificate (recommended)'
  },
  dbs_certificate: {
    key: 'dbs_certificate',
    label: 'DBS Certificate',
    required: false,
    public: false,
    description: 'Enhanced DBS check (recommended for higher trust)'
  },
  driving_licence: {
    key: 'driving_licence',
    label: 'Driving Licence',
    required: true,
    public: false,
    description: 'Valid UK driving licence'
  },
  van_insurance: {
    key: 'van_insurance',
    label: 'Van Insurance',
    required: true,
    public: false,
    description: 'Valid van insurance certificate'
  },
  van_registration: {
    key: 'van_registration',
    label: 'Van Registration (V5C)',
    required: true,
    public: false,
    description: 'Vehicle registration document'
  },
  goods_in_transit_insurance: {
    key: 'goods_in_transit_insurance',
    label: 'Goods in Transit Insurance',
    required: false,
    public: false,
    description: 'Insurance for goods being transported (recommended)'
  },
  waste_carrier_licence: {
    key: 'waste_carrier_licence',
    label: 'Waste Carrier Licence',
    required: true,
    public: false,
    description: 'Valid waste carrier licence'
  },
  vehicle_insurance: {
    key: 'vehicle_insurance',
    label: 'Vehicle Insurance',
    required: true,
    public: false,
    description: 'Valid vehicle insurance certificate'
  },
  vehicle_registration: {
    key: 'vehicle_registration',
    label: 'Vehicle Registration',
    required: true,
    public: false,
    description: 'Vehicle registration document (V5C)'
  }
};

export const TRADE_REQUIREMENTS: Record<string, TradeRequirements> = {
  handyman: {
    required_docs: ['public_liability_insurance'],
    optional_docs: ['portfolio_photos']
  },
  plumbing: {
    required_docs: ['public_liability_insurance', 'qualification_or_experience_proof'],
    optional_docs: ['gas_safe_id', 'portfolio_photos']
  },
  electrical: {
    required_docs: ['public_liability_insurance', 'electrical_qualification'],
    optional_docs: ['niceic_napit_membership', 'portfolio_photos']
  },
  cleaning: {
    required_docs: ['public_liability_insurance'],
    optional_docs: ['dbs_certificate']
  },
  transportation: {
    required_docs: ['driving_licence', 'van_insurance', 'van_registration'],
    optional_docs: ['goods_in_transit_insurance']
  },
  waste_removal: {
    required_docs: ['waste_carrier_licence', 'public_liability_insurance', 'vehicle_insurance', 'vehicle_registration'],
    optional_docs: []
  }
};

export const TRADE_LABELS: Record<string, string> = {
  handyman: 'Handyman',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  cleaning: 'Cleaning',
  transportation: 'Transportation',
  waste_removal: 'Waste Removal'
};

export const TRADE_DESCRIPTIONS: Record<string, string> = {
  handyman: 'General repairs, maintenance, and odd jobs',
  plumbing: 'Plumbing installations, repairs, and maintenance',
  electrical: 'Electrical installations, repairs, and testing',
  cleaning: 'Professional cleaning services',
  transportation: 'Moving and transportation services',
  waste_removal: 'Waste collection and disposal services'
};

export function getRequiredDocuments(tradeType: string): DocumentRequirement[] {
  const requirements = TRADE_REQUIREMENTS[tradeType];
  if (!requirements) return [];
  
  return requirements.required_docs.map(key => DOCUMENT_DEFINITIONS[key]).filter(Boolean);
}

export function getOptionalDocuments(tradeType: string): DocumentRequirement[] {
  const requirements = TRADE_REQUIREMENTS[tradeType];
  if (!requirements) return [];
  
  return requirements.optional_docs.map(key => DOCUMENT_DEFINITIONS[key]).filter(Boolean);
}

export function getAllDocuments(tradeType: string): DocumentRequirement[] {
  return [...getRequiredDocuments(tradeType), ...getOptionalDocuments(tradeType)];
}